import { forkJoin, Observable, throwError as observableThrowError } from 'rxjs';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import * as HttpStatus from 'http-status-codes';
import { extend, snakeCase } from 'lodash';
import { v4 } from 'uuid';
import { BrowserManagerDataService } from '../browser-manager-data/browser-manager-data.service';
import { ErrorService } from '../error-reporting/error.service';
import {
  IHttpService,
  IResponseInfo,
  RequestMethod,
  ResponseContentType,
} from '../http/http.data';
import { HttpService } from '../http/http.service';
import { LocalizationService } from '../localization/localization.service';
import { DateTimeService } from 'i2g-ng-utils';
import { LoggerService } from '../shared/services/logger/logger.service';
import { TrackingDispatcher } from '../tracking/tracking.dispatcher';
import { AuthDataDispatcher } from './auth-data.dispatcher';
import { AuthDataService } from './auth-data.service';
import { AuthDataStore } from './auth-data.store';
import { catchError, flatMap, map, share, take } from 'rxjs/operators';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { environment } from '../../../environments/environment';

export const ACCEPT_VERSION_HEADER = 'x-i2g-accept-version';
export const CORRELATION_ID_KEY = 'x-i2g-correlation-id';
export const X_CACHED_KEYS_KEY = 'x-cached-keys';
export const ENTITY_ACCEPT_VERSION_HEADER = 'x-i2g-entity-version';

@Injectable()
export class AuthHttpService implements IHttpService {
  private _baseUrl: string = environment.gatewayUrl;
  private _cachedKeys: { [url: string]: string } = {};

  baseAuthHeaders$: Observable<any> = this._browserManagerDataService
    .getBrowserInfoHeaders()
    .pipe(
      map((browserInfoHeaders: any) => {
        return extend(browserInfoHeaders, {
          'x-i2g-locale': this._localizationService.getLocale(),
          'x-i2g-timezone': this._dateTimeService.getTimezone(),
        });
      }),
      take(1)
    );

  constructor(
    private _httpService: HttpService,
    private _authDataService: AuthDataService,
    private _authDataStore: AuthDataStore,
    private _authDataDispatcher: AuthDataDispatcher,
    private _dateTimeService: DateTimeService,
    private _loggerService: LoggerService,
    private _localizationService: LocalizationService,
    private _trackingDispatcher: TrackingDispatcher,
    private _errorService: ErrorService,
    private _browserManagerDataService: BrowserManagerDataService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  get(
    url: string,
    params: any = null,
    headers: any = {},
    responseContentType: ResponseContentType = 'json',
    knownErrorCodes: number[] = []
  ): Observable<IResponseInfo> {
    return this.handleRequest(
      'GET',
      url,
      params,
      headers,
      false,
      responseContentType,
      knownErrorCodes
    );
  }

  post(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json',
    knownErrorCodes: number[] = []
  ): Observable<IResponseInfo> {
    return this.handleRequest(
      'POST',
      url,
      body,
      headers,
      storeRequest,
      responseContentType,
      knownErrorCodes
    );
  }

  put(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json',
    knownErrorCodes: number[] = []
  ): Observable<IResponseInfo> {
    return this.handleRequest(
      'PUT',
      url,
      body,
      headers,
      storeRequest,
      responseContentType,
      knownErrorCodes
    );
  }

  delete(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json',
    knownErrorCodes: number[] = []
  ): Observable<IResponseInfo> {
    return this.handleRequest(
      'DELETE',
      url,
      body,
      headers,
      storeRequest,
      responseContentType,
      knownErrorCodes
    );
  }

  handleRequest(
    method: RequestMethod,
    url: string,
    body: any,
    headers: any,
    storeRequest: boolean,
    responseContentType: ResponseContentType,
    knownErrorCodes: number[]
  ): Observable<IResponseInfo> {
    return this._authDataStore.isAuthenticated().pipe(
      flatMap((authenticated: boolean) => {
        if (authenticated) {
          url = url.match(/^https?:\/\//)
            ? url
            : Location.joinWithSlash(this._baseUrl, url);

          let correlationId: string = v4();

          return this.sendRequest(
            method,
            url,
            body,
            headers,
            correlationId,
            storeRequest,
            responseContentType
          ).pipe(
            catchError((initialError) => {
              if (initialError[0] === HttpStatus.UNAUTHORIZED) {
                correlationId = v4();

                return this._authDataService.refreshAccessToken().pipe(
                  flatMap((accessToken: string) => {
                    if (accessToken) {
                      return this.sendRequest(
                        method,
                        url,
                        body,
                        headers,
                        correlationId,
                        storeRequest,
                        responseContentType
                      ).pipe(
                        catchError((err) =>
                          this.catchErrors(err, correlationId, knownErrorCodes)
                        )
                      );
                    }

                    return this.catchErrors(
                      initialError,
                      correlationId,
                      knownErrorCodes
                    );
                  }),
                  share()
                );
              }

              return this.catchErrors(
                initialError,
                correlationId,
                knownErrorCodes
              );
            }),
            share()
          );
        } else {
          this._loggerService.log(
            'attempted to send a request while not authorized.'
          );

          return observableThrowError({ authenticated: false });
        }
      }),
      share()
    );
  }

  sendRequest(
    method: RequestMethod,
    url: string,
    body: any,
    headers: any,
    correlationId: string,
    storeRequest?: boolean,
    responseContentType?: ResponseContentType
  ): Observable<any> {
    return forkJoin(
      this._authDataStore.getAccountId(),
      this._authDataStore.getUserId(),
      this._authDataStore.getAccessToken()
    ).pipe(
      flatMap(([accountId, userId, accessToken]: string[]) =>
        this.constructHeaders(
          url,
          headers,
          correlationId,
          accountId,
          userId,
          accessToken
        )
      ),
      flatMap((requestHeaders: any) => {
        this._loggerService.log(
          'sending',
          method,
          'request to url:',
          url,
          'with body:',
          body,
          'and headers:',
          requestHeaders,
          'and correlation id:',
          correlationId
        );

        return this._httpService.request(
          method,
          url,
          body,
          requestHeaders,
          storeRequest,
          responseContentType
        );
      }),
      map(
        ({
          headers: headersResponse,
          body: bodyResponse,
          status,
        }: IResponseInfo): IResponseInfo => {
          this._loggerService.log(
            'received response from request (correlation id:',
            correlationId,
            '). response body:',
            bodyResponse
          );

          const cachedKeys = headersResponse.get(X_CACHED_KEYS_KEY);

          if (cachedKeys) {
            this._cachedKeys[url] = cachedKeys;
          }

          return { headers: headersResponse, body: bodyResponse, status };
        }
      ),
      share()
    );
  }

  constructHeaders(
    url: string,
    headers: any = {},
    correlationId: string,
    accountId: string,
    userId: string,
    accessToken: string
  ): Observable<any> {
    const baseHeaders = {
      Authorization: `Bearer ${accessToken}`,
      'x-authenticated-account-id': accountId,
      'x-authenticated-user-id': userId,
      [ACCEPT_VERSION_HEADER]: '1.0.0',
      [CORRELATION_ID_KEY]: correlationId,
    };
    const cachedKey = this._cachedKeys[url];

    if (cachedKey) {
      baseHeaders[X_CACHED_KEYS_KEY] = cachedKey;
    }

    return this.baseAuthHeaders$.pipe(
      map((authHeaders: any) => extend(baseHeaders, authHeaders, headers))
    );
  }

  catchErrors(
    error: any[],
    correlationId?: string,
    knownErrorCodes?: number[]
  ): Observable<any> {
    const [status, data = {}] = error;

    let errorData;

    try {
      errorData = JSON.parse(data.error || {});
    } catch {
      errorData = data.error || {};
    }

    const errorBody = errorData.error || {};
    const internalCode = errorBody.code || data.status;
    const description = errorBody.description || '';

    this._loggerService.log(
      'request error for correlation id:',
      correlationId,
      'error:',
      error,
      'error data:',
      errorData
    );

    this._trackingDispatcher.trackEvent(
      this._trackingObjectBuilder.buildNetwork({
        url: data.url,
        http_code: data.status,
        correlation_id: correlationId,
        http_message: data.statusText,
        description,
        internal_code: internalCode,
      }),
      this._trackingActionBuilder.buildError()
    );

    // if unauthorized after retry, logout
    if (status === HttpStatus.UNAUTHORIZED) {
      this._authDataDispatcher.logout(true);

      return observableThrowError({ authenticated: false });
    } else if (status === HttpStatus.BAD_REQUEST) {
      const expectedError = (knownErrorCodes || []).includes(internalCode);

      if (!expectedError) {
        const customGroupingKey = `400_${internalCode}_${snakeCase(
          description
        )}`;

        this._errorService.alertSystem(
          new Error(`bad request to url: ${data.url}`),
          {
            httpStatus: status,
            error: errorBody,
            customGroupingKey,
            correlationId,
          }
        );
      }
    }

    this._loggerService.log(
      'other error',
      error,
      'for correlation id:',
      correlationId
    );

    return observableThrowError([status, errorBody, error, correlationId]);
  }
}
